/**
 * AudioRecorder — chunked upload + Groq Whisper transcription
 *
 * Design:
 *  - MediaRecorder with 5s timeslice: each chunk uploaded to backend immediately
 *    while candidate is still speaking. Even a tab-close only loses the last 5s.
 *  - On stop: POST /api/interview/audio-assemble → server assembles all chunks,
 *    calls Groq Whisper, returns real transcript.
 *  - onTranscriptionComplete called with Groq transcript + audioFileUrl.
 *  - No Web Speech API. No Google dependency. No live preview text.
 *  - Candidate sees no status changes — just "Recording active" pill.
 */
import React, {
  useState, useRef, useEffect, useCallback,
  forwardRef, useImperativeHandle, memo,
} from 'react';
import { cn } from '@/lib/utils';
import { Mic } from 'lucide-react';
import { uploadAudioChunk, assembleAndTranscribe } from '../services/apiClient';

const MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
];

function extForMime(mimeType) {
  const t = String(mimeType || '').toLowerCase();
  if (t.includes('audio/mp4')) return 'mp4';
  if (t.includes('audio/ogg')) return 'ogg';
  return 'webm';
}

function getSupportedMime() {
  return MIME_TYPES.find(t => {
    try { return MediaRecorder.isTypeSupported(t); } catch { return false; }
  }) || null;
}

const CHUNK_INTERVAL_MS = 5000; // upload a chunk every 5 seconds

const AudioRecorder = forwardRef(function AudioRecorder(
  {
    stream,
    onTranscriptionComplete,
    autoStartKey,
    autoStart = true,
    onRecordingStart,
    onRecordingStop,
    onRequestStreamRefresh,
    sessionId,
    questionId,
  },
  ref
) {
  const [recState, setRecState] = useState('idle'); // idle | recording | finalising
  const [error, setError]       = useState('');

  const mediaRecRef    = useRef(null);
  const mimeRef        = useRef(null);
  const stateRef       = useRef('idle');
  const autoKeyRef     = useRef(null);
  /**
   * Which recording currently owns the shared refs.
   *
   * A question's onstop handler runs long after it stopped — it waits on chunk
   * uploads and a Groq transcription. By then the next question is already
   * recording, and anything that handler writes to stateRef or mediaRecRef
   * belongs to a recording that has finished. Each start takes a new
   * generation; a handler only touches shared state while it still holds the
   * current one.
   */
  const genRef         = useRef(0);
  const chunkIndexRef  = useRef(0);
  const uploadQueueRef = useRef(Promise.resolve()); // serialise chunk uploads

  /* ── upload one chunk (fire-and-forget with retry) ───────────────────── */
  const enqueueChunkUpload = useCallback((blob, index) => {
    uploadQueueRef.current = uploadQueueRef.current.then(async () => {
      const MAX = 3;
      for (let attempt = 1; attempt <= MAX; attempt++) {
        try {
          await uploadAudioChunk(blob, {
            sessionId,
            questionId,
            chunkIndex: index,
            mimeType: mimeRef.current || '',
          });
          return;
        } catch (err) {
          if (attempt === MAX) {
            console.warn(`[AudioRecorder] Chunk ${index} upload failed after ${MAX} attempts:`, err.message);
          } else {
            await new Promise(r => setTimeout(r, 1000 * attempt));
          }
        }
      }
    });
  }, [sessionId, questionId]);

  /* ── startRecording ──────────────────────────────────────────────────── */
  const startRecording = useCallback(async () => {
    if (stateRef.current !== 'idle') return;

    const hasLiveTracks = (s) => {
      try { return (s?.getAudioTracks?.() || []).some(t => t.readyState === 'live'); } catch { return false; }
    };

    let workingStream = stream;

    if (!workingStream?.active || !hasLiveTracks(workingStream)) {
      if (onRequestStreamRefresh) {
        try { workingStream = await onRequestStreamRefresh(); } catch {}
      }
      if (!workingStream?.active || !hasLiveTracks(workingStream)) {
        setError('Microphone not available.');
        return;
      }
    }

    const mimeType = getSupportedMime();
    if (!mimeType) {
      setError('This browser does not support audio recording.');
      return;
    }

    setError('');
    chunkIndexRef.current = 0;
    uploadQueueRef.current = Promise.resolve();

    try {
      const liveTracks  = workingStream.getAudioTracks().filter(t => t.readyState === 'live');
      const audioStream = new MediaStream(liveTracks);
      const mr          = new MediaRecorder(audioStream, { mimeType });
      mimeRef.current   = mimeType;
      mediaRecRef.current = mr;

      mr.ondataavailable = (e) => {
        if (!e.data || e.data.size < 10) return;
        const idx = chunkIndexRef.current++;
        enqueueChunkUpload(e.data, idx);
      };

      mr.start(CHUNK_INTERVAL_MS);
      genRef.current += 1;          // this recording now owns the shared refs
      stateRef.current = 'recording';
      setRecState('recording');
      onRecordingStart?.();
    } catch (err) {
      console.error('[AudioRecorder] start error:', err);
      setError(`Could not start recording: ${err.message}`);
    }
  }, [stream, onRecordingStart, onRequestStreamRefresh, enqueueChunkUpload]);

  /* ── stopRecording ───────────────────────────────────────────────────── */
  const stopRecording = useCallback(() => {
    if (stateRef.current !== 'recording') return;
    stateRef.current = 'finalising';
    setRecState('finalising');

    // ── Capture EVERYTHING before onRecordingStop fires ────────────────
    // onRecordingStop() → InterviewPage advances to next question →
    // next question's startRecording() runs → resets uploadQueueRef +
    // chunkIndexRef to 0. If we read these AFTER onRecordingStop, we get
    // the next question's values, causing audio mismatch.
    const capturedGen        = genRef.current;         // who we are
    const capturedQueue      = uploadQueueRef.current; // Q1's pending uploads
    const capturedChunkCount = chunkIndexRef.current;  // Q1's chunk count so far
    const capturedMime       = mimeRef.current;
    const capturedSessionId  = sessionId;
    const capturedQuestionId = questionId;

    onRecordingStop?.(); // may reset uploadQueueRef + chunkIndexRef for Q2

    const mr = mediaRecRef.current;
    if (!mr || mr.state === 'inactive') {
      if (genRef.current === capturedGen) {
        stateRef.current = 'idle';
        setRecState('idle');
      }
      onTranscriptionComplete({ transcription: '', audioFileUrl: 'N/A', isEmpty: true });
      return;
    }

    mr.onstop = async () => {
      // onstop fires ONE final ondataavailable before this — that chunk is
      // already in the captured queue via enqueueChunkUpload (called in
      // ondataavailable before onstop). Wait for the captured queue only —
      // never the shared ref which may already belong to Q2.
      await capturedQueue;

      // Final chunk count: capturedChunkCount + the final chunk that fired
      // in ondataavailable just before onstop. chunkIndexRef was already
      // incremented by that last ondataavailable call before onstop ran,
      // but since startRecording() may have reset it, we use a local counter.
      // The assembled file uses the directory key (sessionId-questionId) so
      // even if count is slightly off, the server assembles all files in the dir.
      const finalTotal = capturedChunkCount + 1; // +1 for the final flush chunk

      let transcript          = '';
      let audioFileUrl        = 'N/A';
      let transcriptionFailed = false;

      if (capturedSessionId && capturedQuestionId) {
        try {
          const result = await assembleAndTranscribe({
            sessionId:   capturedSessionId,
            questionId:  capturedQuestionId,
            totalChunks: finalTotal,
            mimeType:    capturedMime,
          });
          transcript          = result.transcript          || '';
          audioFileUrl        = result.audioFileUrl        || 'N/A';
          transcriptionFailed = result.transcriptionFailed || false;

          if (transcriptionFailed) {
            console.warn('[AudioRecorder] Groq failed — background queue will retry.');
          }
        } catch (err) {
          console.error('[AudioRecorder] assembleAndTranscribe error:', err.message);
          transcriptionFailed = true;
        }
      }

      // The next question may already be recording by now. Only stand the
      // recorder down if this recording is still the current one — otherwise
      // this marks a live recording as idle, and its Submit button silently
      // stops working.
      if (genRef.current === capturedGen) {
        stateRef.current = 'idle';
        setRecState('idle');
      }

      // Always reported, whoever owns the recorder now — the answer still has
      // to reach InterviewPage.
      onTranscriptionComplete({
        transcription: transcript || (transcriptionFailed ? '[Transcription pending]' : ''),
        audioFileUrl,
        isEmpty: !transcript,
      });
    };

    mr.stop();
  }, [onRecordingStop, onTranscriptionComplete, sessionId, questionId]);

  /* ── imperative handle ───────────────────────────────────────────────── */
  useImperativeHandle(ref, () => ({
    stopRecording,
    isRecording: () => stateRef.current === 'recording',
  }));

  /* ── auto-start when question changes ────────────────────────────────── */
  useEffect(() => {
    if (!autoStart || !autoStartKey || !stream?.active) return;
    if (autoKeyRef.current === autoStartKey) return;

    if (stateRef.current === 'recording' || stateRef.current === 'finalising') {
      try { if (mediaRecRef.current?.state === 'recording') mediaRecRef.current.stop(); } catch {}
      stateRef.current = 'idle';
      setRecState('idle');
    }

    const t = setTimeout(() => {
      autoKeyRef.current = autoStartKey;
      if (stateRef.current !== 'idle') { stateRef.current = 'idle'; setRecState('idle'); }
      startRecording();
    }, 200); // reduced from 800ms — no Web Speech API connection needed

    return () => clearTimeout(t);
  }, [autoStartKey, stream, autoStart, startRecording]);

  /* ── cleanup on unmount ──────────────────────────────────────────────── */
  useEffect(() => () => {
    try {
      if (mediaRecRef.current?.state === 'recording') mediaRecRef.current.stop();
    } catch {}
  }, []);

  const isRecording  = recState === 'recording';
  const isFinalising = recState === 'finalising';

  return (
    <div className="w-full space-y-2">
      <div className="flex justify-center">
        <div className={cn(
          'inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold',
          isRecording  && 'bg-emerald-500/15 text-emerald-400',
          isFinalising && 'bg-primary/10 text-primary/70',
          !isRecording && !isFinalising && 'bg-muted text-muted-foreground',
        )}>
          {isRecording && (
            <>
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" />
              Recording — speak your answer clearly
            </>
          )}
          {isFinalising && (
            <>
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
              Saving answer…
            </>
          )}
          {!isRecording && !isFinalising && (
            <><Mic className="h-3.5 w-3.5" />Preparing microphone…</>
          )}
        </div>
      </div>
      {error && <p className="text-center text-xs text-destructive">{error}</p>}
    </div>
  );
});

export default memo(AudioRecorder);
